import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { formatCurrencyCompact } from "../../utils/formatters";

const COLORS = {
    cash: "#2563EB",
    investments: "#059669",
    credit: "#DC2626",
    loans: "#D97706",
};

function CustomTooltip({ active, payload }) {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-md">
                <p className="text-gray-500 text-xs mb-1">{data.name}</p>
                <p className="text-gray-900 font-semibold">{formatCurrencyCompact(data.value)}</p>
                <p className="text-gray-500 text-xs">{data.percentage.toFixed(1)}%</p>
            </div>
        );
    }
    return null;
}

function CustomLegend({ payload }) {
    return (
        <div className="flex flex-wrap justify-center gap-4 mt-4">
            {payload.map((entry, index) => (
                <div key={index} className="flex items-center gap-2">
                    <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-gray-700 text-sm">{entry.value}</span>
                </div>
            ))}
        </div>
    );
}

export default function AllocationChart({ netWorthData }) {
    if (!netWorthData) {
        return null;
    }

    const { cashBalance, investmentBalance, creditBalance, loanBalance, totalAssets, totalLiabilities } = netWorthData;

    const total = (cashBalance || 0) + (investmentBalance || 0) + (creditBalance || 0) + (loanBalance || 0);

    if (total === 0) {
        return (
            <div className="card p-6">
                <p className="text-sm font-medium text-gray-700 mb-4">Asset allocation</p>
                <p className="text-gray-500 text-center py-8">No allocation data available</p>
            </div>
        );
    }

    const data = [
        { name: "Cash", value: cashBalance || 0, color: COLORS.cash },
        { name: "Investments", value: investmentBalance || 0, color: COLORS.investments },
        { name: "Credit cards", value: creditBalance || 0, color: COLORS.credit },
        { name: "Loans", value: loanBalance || 0, color: COLORS.loans },
    ].filter(item => item.value > 0).map(item => ({
        ...item,
        percentage: (item.value / total) * 100
    }));

    return (
        <div className="card p-6">
            <p className="text-sm font-medium text-gray-700 mb-4">Asset allocation</p>
            <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                            ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
                <div className="text-center">
                    <p className="text-xs text-gray-500">Total assets</p>
                    <p className="text-lg font-semibold text-gray-900">{formatCurrencyCompact(totalAssets)}</p>
                </div>
                <div className="text-center">
                    <p className="text-xs text-gray-500">Total liabilities</p>
                    <p className="text-lg font-semibold text-danger-500">{formatCurrencyCompact(totalLiabilities)}</p>
                </div>
            </div>
        </div>
    );
}
